import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * TourProvider — coordinates the mandatory post-briefing guided tour across
 * screens (dashboard → flow editor → invite members → channel → inbox).
 *
 * The cursor (`stage`) is persisted per account in localStorage so the tour
 * survives navigation and refreshes. Each screen reads `useTour()` to render
 * its stage's Spotlight and to advance when the real action is done. Superadmin
 * gets a hidden bypass so the team isn't trapped while testing.
 */
// "members" is a planned stage whose host isn't built yet, so the active path
// goes flow → channel → inbox. Re-insert "members" once its host exists.
export const TOUR_STAGES = ["flow", "channel", "inbox"];

const TourContext = createContext(null);
const keyFor = (clientId) => `tw_tour_${clientId}`;

function read(clientId) {
  if (!clientId) return { status: "idle", stage: null };
  try {
    const raw = localStorage.getItem(keyFor(clientId));
    return raw ? JSON.parse(raw) : { status: "idle", stage: null };
  } catch {
    return { status: "idle", stage: null };
  }
}

export function TourProvider({ clientId, children }) {
  const { isSuperAdmin } = useAuth();
  const superadmin = typeof isSuperAdmin === "function" ? isSuperAdmin() : false;

  const [state, setState] = useState(() => read(clientId));

  const write = useCallback(
    (next) => {
      setState(next);
      try {
        localStorage.setItem(keyFor(clientId), JSON.stringify(next));
      } catch {
        /* ignore */
      }
    },
    [clientId],
  );

  // Start the tour at a given stage, but only once (never re-opens after done).
  const start = useCallback(
    (stage = "flow") => {
      setState((prev) => {
        if (prev.status === "done" || prev.status === "active") return prev;
        const next = { status: "active", stage };
        try {
          localStorage.setItem(keyFor(clientId), JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return next;
      });
    },
    [clientId],
  );

  const goToStage = useCallback(
    (stage) => write({ status: "active", stage }),
    [write],
  );

  const advanceStage = useCallback(() => {
    setState((prev) => {
      const i = TOUR_STAGES.indexOf(prev.stage);
      const nextStage = TOUR_STAGES[i + 1];
      const next = nextStage
        ? { status: "active", stage: nextStage }
        : { status: "done", stage: null };
      try {
        localStorage.setItem(keyFor(clientId), JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, [clientId]);

  const endTour = useCallback(
    () => write({ status: "done", stage: null }),
    [write],
  );

  const value = useMemo(
    () => ({
      stage: state.status === "active" ? state.stage : null,
      isActive: state.status === "active",
      isDone: state.status === "done",
      superadmin,
      start,
      goToStage,
      advanceStage,
      endTour,
    }),
    [state, superadmin, start, goToStage, advanceStage, endTour],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

// Standalone helpers for screens mounted on a sibling route (e.g. the flow
// editor) that aren't inside the dashboard's TourProvider. They read/write the
// same localStorage cursor, so state stays in sync across navigations.
export function getTourStage(clientId) {
  const s = read(clientId);
  return s.status === "active" ? s.stage : null;
}

export function advanceTour(clientId) {
  const prev = read(clientId);
  const i = TOUR_STAGES.indexOf(prev.stage);
  const nextStage = TOUR_STAGES[i + 1];
  const next = nextStage
    ? { status: "active", stage: nextStage }
    : { status: "done", stage: null };
  try {
    localStorage.setItem(keyFor(clientId), JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next.stage;
}

export function useTour() {
  return (
    useContext(TourContext) || {
      stage: null,
      isActive: false,
      isDone: false,
      superadmin: false,
      start: () => {},
      goToStage: () => {},
      advanceStage: () => {},
      endTour: () => {},
    }
  );
}
